import * as React from "react";
import { useAtomValue } from "jotai";
// Vite `?raw` imports: the library's own UMD bundle + CSS as plain text, so
// they can be inlined into a sandboxed iframe's srcDoc rather than loaded as
// same-origin <script src> tags (see the safety-model note below). Vendored
// into ./vendor rather than deep-imported from node_modules — see that
// folder's README for why (short version: a node_modules relative path
// resolved fine on the host but not inside the Docker dev container).
import rrwebPlayerBundle from "./vendor/rrweb-player.umd.min.cjs?raw";
import rrwebPlayerCss from "./vendor/rrweb-player.style.css?raw";
import { useI18n } from "../../../lib/i18n";
import { API_BASE_URL } from "../../../lib/api/pro-api.client";
import { isDarkModeAtom } from "../../../lib/theme/atoms";
import * as s from "./ReplayPlayer.css";

interface ReplayPlayerProps {
  /** Chunk URLs in seq order — presigned S3 URLs in deployed envs, or a pro-api-proxied local-blob URL in dev (see fetchAnalyticsSessionReplay). */
  chunkUrls: string[];
  /** Attached only to our own pro-api-proxied URLs (local dev) — the blob-proxy route requires auth. Never sent to an external URL (e.g. presigned S3). */
  accessToken: string;
}

// rrweb-player's controller bar (timeline/play/speed) is a fixed 80px tall,
// added BELOW the content viewport height it's constructed with — confirmed
// from vendor/rrweb-player.style.css's ".rr-controller { height: 80px }".
const CONTROLLER_HEIGHT = 80;
// Fallback only — used if a recording somehow has no Meta event to read a
// real aspect ratio from (shouldn't happen; every session starts with one).
const FALLBACK_ASPECT_RATIO = 16 / 9;
// Visual framing around `.rr-player__frame` so the recording's bounds read
// clearly against the section background — subtracted from the width/height
// passed to the Player constructor (see frameWidth/contentHeight below) so
// the margin fits inside the container's allotted space instead of
// overflowing it.
const FRAME_MARGIN = 5;
const FRAME_BORDER_WIDTH = 2;
// Same yellow as the "System" nav module's icon badge (NavigationBar.style.css.ts's `yellow500`).
const FRAME_BORDER_COLOR = "#FFCF51";
// Matches the app's section-card radius (CollapsibleSection.style.css.ts's
// `sectionCard`), not an arbitrary value, so the framed viewport reads as
// consistent with every other rounded panel in the app.
const FRAME_BORDER_RADIUS = 12;

function extractAspectRatio(events: unknown[]): number {
  const meta = events.find(
    (e): e is { type: number; data: { width?: number; height?: number } } =>
      typeof e === "object" && e !== null && (e as { type?: number }).type === 4,
  );
  const w = meta?.data.width;
  const h = meta?.data.height;
  if (!w || !h) return FALLBACK_ASPECT_RATIO;
  return w / h;
}

/**
 * Renders rrweb-player inside a sandboxed iframe rather than inline in the
 * host page's DOM.
 *
 * **Revised security model (verified live, differs from this project's
 * original plan — flag for security-review)**: the original design wanted
 * `sandbox="allow-scripts"` on the outer iframe WITHOUT `allow-same-origin`,
 * so the iframe would get an opaque origin unable to reach this app's
 * cookies/localStorage. In practice, rrweb-player has its OWN internal
 * protective mechanism — `rrweb-snapshot.createSandboxedIframe()` — which
 * rebuilds the actual recorded DOM into a *second, nested* iframe that it
 * creates with `sandbox="allow-same-origin"` and deliberately no
 * `allow-scripts`, so recorded content genuinely cannot execute scripts
 * there. That nested iframe requires the OUTER hosting frame to itself have
 * a stable (non-opaque) origin to create and access it — confirmed live: with
 * the outer frame opaque-origin (no `allow-same-origin`), rrweb-snapshot
 * threw `createSandboxedIframe() requires root to be connected to a
 * document` on every attempt, for every session tested, not a timing fluke.
 *
 * So the outer iframe here has `allow-scripts allow-same-origin` (needed
 * only so rrweb-player's own code can run and manage its nested iframe) —
 * the REAL script-execution boundary is rrweb's inner nested iframe, which
 * this component never touches directly. Still strictly more isolated than
 * most vendors' default (rendering rrweb-player inline in the dashboard's own
 * DOM with no iframe boundary at all). The player bundle + CSS are inlined as
 * text (not loaded via same-origin `<script src>`), and the event JSON is
 * posted in via postMessage after load rather than embedded in the srcDoc
 * string.
 *
 * **Sizing**: rrweb-player scales the recorded content (fixed to its
 * original captured dimensions) to fit within whatever width/height it's
 * constructed with, via a CSS transform — preserving the recording's aspect
 * ratio but NOT the frame's. Passing an arbitrary frame size (e.g. just the
 * container's width with a fixed height) almost never matches the
 * recording's own aspect ratio, so the scaled content ends up letterboxed —
 * and `.rr-player`'s own hardcoded `background: white` (vendor CSS) shows
 * through the gap regardless of this app's theme. Fixed by deriving the
 * frame height FROM the container width and the recording's real aspect
 * ratio (read from its Meta event), so the frame's aspect ratio matches the
 * content exactly and there's no letterboxing to show a gap in. The
 * container's height then reflects content height + the controller's fixed
 * 80px, growing/shrinking together as the container resizes.
 */
export function ReplayPlayer({ chunkUrls, accessToken }: ReplayPlayerProps) {
  const { t } = useI18n();
  const hostRef = React.useRef<HTMLDivElement>(null);
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [events, setEvents] = React.useState<unknown[] | null>(null);
  const [containerWidth, setContainerWidth] = React.useState(0);

  React.useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const update = () => setContainerWidth(el.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    async function loadChunks() {
      try {
        const chunkArrays = await Promise.all(
          chunkUrls.map(async (url) => {
            const isOwnProxy = url.startsWith(API_BASE_URL);
            const res = await fetch(url, {
              headers: isOwnProxy ? { Authorization: `Bearer ${accessToken}` } : undefined,
            });
            if (!res.ok) throw new Error(`Failed to fetch replay chunk (${res.status})`);
            return (await res.json()) as unknown[];
          }),
        );
        if (!cancelled) setEvents(chunkArrays.flat());
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : t("pro.system.productAnalytics.replay.loadError"));
        }
      }
    }

    void loadChunks();
    return () => {
      cancelled = true;
    };
  }, [chunkUrls, accessToken, t]);

  const aspectRatio = React.useMemo(
    () => (events ? extractAspectRatio(events) : FALLBACK_ASPECT_RATIO),
    [events],
  );
  // The frame itself is sized smaller than the container by 2*FRAME_MARGIN
  // on each axis, leaving exact room for the margin without overflowing the
  // container's own bounds.
  const frameWidth = containerWidth > FRAME_MARGIN * 2 ? containerWidth - FRAME_MARGIN * 2 : 0;
  const contentHeight = frameWidth > 0 ? Math.round(frameWidth / aspectRatio) : 0;
  const totalHeight =
    contentHeight > 0 ? contentHeight + FRAME_MARGIN * 2 + CONTROLLER_HEIGHT : 0;

  // Reactive to the app's light/dark toggle (isDarkModeAtom) — CSS custom
  // properties (this app's theme tokens) automatically reflect whichever
  // theme is active, but the iframe is a separate document that can't just
  // reference the parent's custom properties, so the resolved values are
  // threaded through postMessage instead. rrweb-player's own vendor CSS
  // hardcodes white backgrounds and dark text/icon colors throughout its
  // controller (`.rr-controller`, `.rr-timeline__time`, the play/speed
  // button icons) — fine against its own default white chrome, invisible
  // against a dark background. Both bg and fg are needed to override that
  // consistently, not just the background. Reading getComputedStyle here is
  // safe (not stale) because applyThemeToDocument (src/app/lib/theme/atoms.ts)
  // applies the class + inline shell colors synchronously in the same atom
  // write that updates isDarkModeAtom, before this component ever re-renders.
  const isDarkMode = useAtomValue(isDarkModeAtom);
  const theme = React.useMemo(() => {
    if (typeof window === "undefined") {
      return { bg: "#1e1e1e", fg: "#f9fafb", isDark: true };
    }
    return {
      bg: getComputedStyle(document.body).backgroundColor || "#1e1e1e",
      fg: getComputedStyle(document.body).color || "#f9fafb",
      isDark: isDarkMode,
    };
  }, [isDarkMode]);

  // The player is constructed exactly once (the iframe script has no
  // "already initialized" guard of its own — re-posting the full events
  // payload would construct a second instance stacked on top of the first).
  // Later container-resize events instead post a lightweight "resize"
  // message that updates the EXISTING instance's props via Svelte's
  // reactive $set, so shrinking/growing the container (issue: clipping on
  // small screens) reflows the already-built player instead of requiring a
  // rebuild.
  const postedRef = React.useRef(false);
  const iframeLoadedRef = React.useRef(false);

  const tryConstruct = React.useCallback(() => {
    if (postedRef.current) return;
    if (!iframeLoadedRef.current) return;
    if (!events || contentHeight === 0) return;
    if (!iframeRef.current?.contentWindow) return;
    postedRef.current = true;
    iframeRef.current.contentWindow.postMessage(
      { type: "rrweb-init", events, width: frameWidth, height: contentHeight, totalHeight, aspectRatio, theme },
      "*",
    );
  }, [events, frameWidth, contentHeight, totalHeight, aspectRatio, theme]);

  const handleIframeLoad = React.useCallback(() => {
    iframeLoadedRef.current = true;
    tryConstruct();
  }, [tryConstruct]);

  React.useEffect(() => {
    tryConstruct();
  }, [tryConstruct]);

  // Fires on every later resize once the player already exists — a no-op
  // via the iframe script's own guard until postedRef's initial message has
  // actually landed.
  React.useEffect(() => {
    if (!postedRef.current || contentHeight === 0) return;
    iframeRef.current?.contentWindow?.postMessage(
      { type: "rrweb-resize", width: frameWidth, height: contentHeight, totalHeight, aspectRatio },
      "*",
    );
  }, [frameWidth, contentHeight, totalHeight, aspectRatio]);

  // Fires when the user toggles light/dark mode AFTER the player already
  // exists — theme was previously resolved once at construction time only,
  // so a later toggle never reached the already-built iframe.
  React.useEffect(() => {
    if (!postedRef.current) return;
    iframeRef.current?.contentWindow?.postMessage({ type: "rrweb-theme", theme }, "*");
  }, [theme]);

  const srcDoc = React.useMemo(
    () => `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<style>
  html, body { margin: 0; padding: 0; overflow: hidden; }
  ${rrwebPlayerCss}
  /* Visual framing so the recording's bounds read clearly against the
     section background — static (not theme-dependent), so it's baked in
     here rather than in the theme-override <style> element below. The
     matching margin subtracted from the width/height passed to the Player
     constructor (see frameWidth in the parent component) leaves exact room
     for this without overflowing the iframe's own bounds. box-sizing:
     border-box keeps the border inside the frame's existing sized box
     rather than adding to it. */
  .rr-player__frame {
    margin: ${FRAME_MARGIN}px;
    border: ${FRAME_BORDER_WIDTH}px solid ${FRAME_BORDER_COLOR};
    border-radius: ${FRAME_BORDER_RADIUS}px;
    box-sizing: border-box;
  }
  /* .rr-player is sized to fit the recording's own aspect ratio (see
     fitToAspectRatio below), which is deliberately narrower or shorter than
     the full fullscreen viewport whenever that ratio doesn't match the
     screen's own — same reasoning as letterboxing around a video.
     Corrected model (the previous attempt here — forcing .rr-player itself
     to a fixed inset+margin:auto box — assumed .rr-player kept its own
     explicit (smaller) width/height while fullscreen; confirmed wrong): the
     browser's own :fullscreen UA style actually stretches .rr-player itself
     to fill the screen (width/height:100%, !important, beating our inline
     style), so .rr-player is NOT what needs centering — its child
     .rr-player__frame (which keeps its own explicit, smaller, aspect-fit
     size) is what's left/top-anchored inside that now-full-size parent.
     margin:auto centers the frame horizontally (a normal block element
     centers this way once its parent is wider than it); align-content:center
     on the parent handles vertical centering, since block-level vertical
     margin:auto alone doesn't (align-content for block containers is a
     newer, but widely supported, CSS Box Alignment feature). Declared as
     separate single-selector rules, not shared comma-separated lists — an
     unrecognized vendor-prefixed pseudo-class in a shared list invalidates
     the WHOLE rule in most browsers (confirmed live: Chromium silently
     dropped an earlier version of this rule entirely once
     :-moz-full-screen/:-ms-fullscreen were combined into it alongside the
     standard/webkit ones it does recognize), so each needs its own rule to
     fail independently instead of taking the real ones down with it. */
  .rr-player:fullscreen {
    align-content: center;
  }
  .rr-player:-webkit-full-screen {
    align-content: center;
  }
  .rr-player:-moz-full-screen {
    align-content: center;
  }
  .rr-player:-ms-fullscreen {
    align-content: center;
  }
  .rr-player:fullscreen .rr-player__frame {
    margin: auto;
  }
  .rr-player:-webkit-full-screen .rr-player__frame {
    margin: auto;
  }
  .rr-player:-moz-full-screen .rr-player__frame {
    margin: auto;
  }
  .rr-player:-ms-fullscreen .rr-player__frame {
    margin: auto;
  }
</style>
</head>
<body>
<div id="app"></div>
<script>${rrwebPlayerBundle}</script>
<script>
  var player = null;
  // Created once, content replaced on every theme change — so a later
  // light/dark toggle re-themes the already-built controller instead of only
  // ever applying at construction time.
  var themeStyleEl = null;
  // The recording's own width/height ratio (from its Meta event, computed on
  // the React side) — kept up to date by every init/resize message so the
  // fullscreen handler below can fit the frame to it independently, without
  // waiting on a round trip to the parent.
  var contentAspectRatio = null;
  // Stamped from the same FRAME_MARGIN/CONTROLLER_HEIGHT constants the React
  // component uses (see the static CSS above) — kept as JS values here too
  // so the fullscreen-exit math below can reproduce the exact same
  // frameWidth/contentHeight/totalHeight formulas.
  var FRAME_MARGIN = ${FRAME_MARGIN};
  var CONTROLLER_HEIGHT = ${CONTROLLER_HEIGHT};

  function getFullscreenElement() {
    return (
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    );
  }

  function applyPlayerSize(width, height) {
    if (player && typeof player.$set === 'function' && width > 0 && height > 0) {
      // $set alone updates the outer frame's width/height reactively, but
      // the scale transform on the recorded content itself
      // (.replayer-wrapper) is computed once and doesn't recompute from a
      // prop change alone — triggerResize() is rrweb-player's own exposed
      // method for forcing that recalculation.
      player.$set({ width: width, height: height });
      if (typeof player.triggerResize === 'function') {
        player.triggerResize();
      }
    }
  }

  // Fits a box of the recording's own aspect ratio inside the given
  // available space (scale-to-fit, no stretching) — the same thing the
  // React side does for the normal (non-fullscreen) frame size, so
  // .rr-player__frame's own box always matches the recording's aspect ratio
  // exactly and there's no letterboxing gap between our orange border and
  // the actual (rrweb-player-scaled) video inside it.
  function fitToAspectRatio(maxWidth, maxHeight) {
    if (!contentAspectRatio || maxWidth <= 0 || maxHeight <= 0) {
      return { width: maxWidth, height: maxHeight };
    }
    var heightAtMaxWidth = maxWidth / contentAspectRatio;
    if (heightAtMaxWidth <= maxHeight) {
      return { width: maxWidth, height: heightAtMaxWidth };
    }
    return { width: maxHeight * contentAspectRatio, height: maxHeight };
  }

  // rrweb-player's own fullscreen (expand) button requests fullscreen on
  // .rr-player but never resizes its width/height props to match — the
  // element keeps whatever fixed pixel size it already had, so the browser
  // just renders that same small box inside a much bigger fullscreen
  // backdrop. rrweb-player exposes no CSS or JS hook of its own for this, so
  // this reuses the exact same $set + triggerResize mechanism as a normal
  // container resize, driven by fullscreenchange instead of a
  // ResizeObserver.
  //
  // On exit, rather than restoring a size remembered from before entering
  // fullscreen (which can go stale — e.g. the container was resized while
  // away, or the remembered value was never applied for some other reason),
  // this asks the browser directly for the iframe's OWN current width via
  // window.frameElement (available because the outer iframe has
  // allow-same-origin) and recomputes frameWidth/contentHeight from it
  // exactly like the React side does.
  //
  // Both branches defer their actual measurement by a couple of animation
  // frames rather than reading window.innerWidth/innerHeight (entering) or
  // window.frameElement.clientWidth (exiting) synchronously inside the
  // fullscreenchange handler itself — confirmed live (via a real dispatched
  // event, not a direct call — a plain synchronous $set+triggerResize call
  // from a manual script never picked up the new size no matter how long an
  // in-script delay was added, but the identical call correctly rescaled
  // when triggered by a genuine event) that this event can fire before the
  // browser has finished promoting/demoting the outer iframe's own layout
  // to match the new fullscreen state, so reading geometry immediately
  // measures the OLD size. Two rAFs (one to let the pending layout flush,
  // one to measure after it's actually painted) is the standard defensive
  // pattern for this class of race.
  function handleFullscreenChange() {
    var entering = !!getFullscreenElement();
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if (entering) {
          var fit = fitToAspectRatio(window.innerWidth, Math.max(0, window.innerHeight - CONTROLLER_HEIGHT));
          applyPlayerSize(fit.width, fit.height);
          return;
        }
        if (!window.frameElement) return;
        var restoredFrameWidth = window.frameElement.clientWidth - FRAME_MARGIN * 2;
        if (restoredFrameWidth <= 0) return;
        var restoredHeight = contentAspectRatio ? restoredFrameWidth / contentAspectRatio : 0;
        applyPlayerSize(restoredFrameWidth, restoredHeight);
        if (restoredHeight > 0) {
          setBodyHeight(restoredHeight + FRAME_MARGIN * 2 + CONTROLLER_HEIGHT);
        }
      });
    });
  }
  document.addEventListener('fullscreenchange', handleFullscreenChange);
  document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
  document.addEventListener('mozfullscreenchange', handleFullscreenChange);
  document.addEventListener('MSFullscreenChange', handleFullscreenChange);

  // .rr-player is a floated (float:left), non-flex block — its child frame
  // (.rr-player__frame) has a CSS margin added for visual framing, but the
  // vendor Svelte component sets .rr-player's OWN declared height from only
  // contentHeight + controller height, with no idea that margin exists. A
  // floated element's contribution to its (BFC) parent's auto-height is
  // based on its own declared box, not on descendants that visually spill
  // past it — so with body's height left to auto-size, it undercounts by
  // exactly the frame's top+bottom margin, and body's own overflow:hidden
  // then clips the controller's bottom edge by that same amount. Setting
  // body's height explicitly (from the same totalHeight the parent React
  // component sizes the outer iframe element to) sidesteps that BFC
  // undercount entirely instead of fighting it via more CSS.
  function setBodyHeight(totalHeight) {
    if (typeof totalHeight === 'number' && totalHeight > 0) {
      document.body.style.height = totalHeight + 'px';
    }
  }

  function applyTheme(theme) {
    if (!theme) return;
    if (theme.bg) document.body.style.background = theme.bg;
    if (theme.fg) document.body.style.color = theme.fg;
    if (!theme.bg || !theme.fg) return;

    // rrweb-player's vendor CSS hardcodes a white background and dark
    // text/icon colors throughout the controller — fine against its own
    // default white chrome, invisible once that chrome is re-themed dark.
    // Injected as a real stylesheet (not per-element inline styles) so it
    // covers every instance of each class in one place, with !important to
    // beat the vendor rules' specificity.
    //
    // The progress track needs to read as visibly DIFFERENT from the
    // controller background (it's the empty portion of the scrubber) —
    // giving it the exact same color as the background it sits on made it
    // invisible, leaving only the filled portion/handle floating with
    // nothing to show the rest of the timeline against. Bright enough in
    // dark mode to actually read as a lighter track, not just a faint tint.
    var trackBg = theme.isDark ? 'rgba(255, 255, 255, 0.35)' : '#eee';
    // .rr-progress's vendor CSS also hardcodes a 4px solid WHITE border on
    // its top and bottom (unrelated to its own background) — in dark mode
    // that pair of white bars is what actually reads as "the track has a
    // white background", squeezing our themed background into a thin
    // sliver in the middle. Overriding the border color to match the
    // controller's own background makes those bars blend away instead.
    var overrideCss = '.rr-player, .rr-controller { background: ' + theme.bg + ' !important; }' +
      '.rr-progress { background: ' + trackBg + ' !important; border-top-color: ' + theme.bg + ' !important; border-bottom-color: ' + theme.bg + ' !important; }' +
      '.rr-timeline__time, .rr-controller__btns button { color: ' + theme.fg + ' !important; }' +
      '.rr-controller__btns svg, .rr-controller__btns svg path { fill: ' + theme.fg + ' !important; }' +
      // Selected-state buttons (e.g. the active speed) keep their own
      // blue-fill/white-text look regardless of theme — that's a
      // deliberate "selected" indicator, not chrome that needs re-theming.
      '.rr-controller__btns button.active, .rr-controller__btns button.active svg path { color: #fff !important; fill: #fff !important; }';

    if (!themeStyleEl) {
      themeStyleEl = document.createElement('style');
      document.head.appendChild(themeStyleEl);
    }
    themeStyleEl.textContent = overrideCss;
  }

  window.addEventListener('message', function (e) {
    if (!e.data) return;

    if (e.data.type === 'rrweb-resize') {
      if (e.data.aspectRatio) contentAspectRatio = e.data.aspectRatio;
      // While fullscreen is active, don't shrink the live fullscreen player
      // back down just because the (invisible, normal-flow) container
      // behind it reported a resize — handleFullscreenChange recomputes
      // from the container's actual current size when fullscreen exits.
      if (!getFullscreenElement()) {
        applyPlayerSize(e.data.width, e.data.height);
      }
      setBodyHeight(e.data.totalHeight);
      return;
    }

    if (e.data.type === 'rrweb-theme') {
      applyTheme(e.data.theme);
      return;
    }

    if (e.data.type !== 'rrweb-init' || player) return;
    applyTheme(e.data.theme);
    setBodyHeight(e.data.totalHeight);
    if (e.data.aspectRatio) contentAspectRatio = e.data.aspectRatio;

    // The UMD bundle's CJS interop wraps the real Player class under
    // .default (also available as .Player) rather than exposing it as the
    // global itself.
    var PlayerCtor = (rrwebPlayer && (rrwebPlayer.default || rrwebPlayer.Player)) || rrwebPlayer;
    // Deferred one frame: rrweb-snapshot's internal sandboxed-iframe setup
    // checks that its own root element isConnected, which isn't reliably
    // true yet in the exact same tick as this srcDoc document's own load.
    requestAnimationFrame(function () {
      try {
        player = new PlayerCtor({
          target: document.getElementById('app'),
          props: {
            events: e.data.events,
            // Matches the recording's own aspect ratio (computed on the
            // React side from its Meta event) — no letterboxing, so
            // .rr-player's hardcoded white background never shows through.
            width: e.data.width,
            height: e.data.height,
            autoPlay: false,
            showController: true,
          },
        });
      } catch (err) {
        document.getElementById('app').textContent = 'Failed to initialize replay: ' + (err && err.message);
      }
    });
  });
</script>
</body>
</html>`,
    [],
  );

  let content: React.ReactNode;
  if (loadError) {
    content = <div className={s.errorState}>{loadError}</div>;
  } else if (!events) {
    content = <div className={s.loadingState}>{t("pro.system.productAnalytics.replay.loading")}</div>;
  } else if (events.length === 0) {
    content = <div className={s.loadingState}>{t("pro.system.productAnalytics.replay.empty")}</div>;
  } else if (totalHeight === 0) {
    content = <div className={s.loadingState}>{t("pro.system.productAnalytics.replay.loading")}</div>;
  } else {
    content = (
      <iframe
        ref={iframeRef}
        title={t("pro.system.productAnalytics.replay.iframeTitle")}
        srcDoc={srcDoc}
        sandbox="allow-scripts allow-same-origin"
        // Required for rrweb-player's own fullscreen (expand) button to work
        // at all — the Fullscreen API is gated by this attribute separately
        // from the sandbox tokens above; without it, requestFullscreen()
        // calls from inside the iframe are rejected.
        allowFullScreen
        onLoad={handleIframeLoad}
        className={s.iframe}
        style={{ height: totalHeight }}
      />
    );
  }

  return (
    <div className={s.playerHost} ref={hostRef}>
      {content}
    </div>
  );
}
